import CloseIcon from '@mui/icons-material/Close';
import { Box, Typography } from '@mui/material';
import React, { ReactElement } from 'react';
import { DeleteIcon, EditSquareIcon, OutgoingMailIcon } from 'components/icons';
import {
  deriveLeaderUiStatus,
  leaderRowActions,
  OrgMemberRow,
} from './deriveLeaderUiStatus';

const COLOR_EDIT = '#23305F';
const COLOR_REMOVE = '#9B0000';

export function LeadershipOutlineAction ({
  children,
  icon,
  borderColor,
  color,
  onClick,
  width,
  fullWidth,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  borderColor: string;
  color: string;
  onClick: () => void;
  width?: number;
  fullWidth?: boolean;
}): ReactElement {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0,
        border: `1px solid ${borderColor}`,
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        padding: '10px',
        height: 43,
        width: fullWidth ? '100%' : width ?? 'auto',
        cursor: 'pointer',
        color,
        fontFamily: '\'Poppins\', sans-serif',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '21px',
        '&:hover': {
          backgroundColor: '#FAFAFA',
        },
      }}
    >
      {icon ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'inherit',
            '& svg': { width: 23, height: 23 },
          }}
        >
          {icon}
        </Box>
      ) : null}
      <Typography component="span" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit' }}>
        {children}
      </Typography>
    </Box>
  );
}

export function LeadershipTableRowActions ({
  leader,
  canRemoveAdmin,
  onEdit,
  onRemove,
  onCancel,
  onReInvite,
}: {
  leader: OrgMemberRow;
  canRemoveAdmin?: boolean;
  onEdit: (leader: OrgMemberRow) => void;
  onRemove: (leader: OrgMemberRow) => void;
  onCancel: (leader: OrgMemberRow) => void;
  onReInvite: (leader: OrgMemberRow) => void;
}): ReactElement {
  const a = leaderRowActions(deriveLeaderUiStatus(leader), leader.kind, {
    canRemoveAdmin,
  });

  return (
    <Box
      display="flex"
      alignItems="center"
      gap="16px"
    >
      {a.showEdit ? (
        <LeadershipOutlineAction
          width={95}
          borderColor={COLOR_EDIT}
          color={COLOR_EDIT}
          icon={<EditSquareIcon />}
          onClick={() => onEdit(leader)}
        >
          Edit
        </LeadershipOutlineAction>
      ) : null}
      {a.showRemove ? (
        <LeadershipOutlineAction
          width={101}
          borderColor={COLOR_REMOVE}
          color={COLOR_REMOVE}
          icon={<DeleteIcon />}
          onClick={() => onRemove(leader)}
        >
          Remove
        </LeadershipOutlineAction>
      ) : null}
      {a.showCancel ? (
        <LeadershipOutlineAction
          width={101}
          borderColor={COLOR_REMOVE}
          color={COLOR_REMOVE}
          icon={<CloseIcon sx={{ fontSize: 23 }} />}
          onClick={() => onCancel(leader)}
        >
          Cancel
        </LeadershipOutlineAction>
      ) : null}
      {a.showReInvite ? (
        <LeadershipOutlineAction
          width={101}
          borderColor={COLOR_EDIT}
          color={COLOR_EDIT}
          icon={<OutgoingMailIcon />}
          onClick={() => onReInvite(leader)}
        >
          Re-invite
        </LeadershipOutlineAction>
      ) : null}
    </Box>
  );
}
